import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  PlayIcon,
  TrashIcon,
  EyeIcon,
  CloudIcon,
  ShieldExclamationIcon,
  BugAntIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { domainInvestigationService, Investigation, CollectorInfo } from '../services/domainInvestigation';
import { companiesService, Company } from '../services/companies';
import { leakedCredentialsService } from '../services/leakedCredentials';
import InvestigationDetailsModal from '../components/InvestigationDetailsModal';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../services/users';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/i18nFormat';

const DomainInvestigation: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedCollectors, setSelectedCollectors] = useState<string[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [investigations, setInvestigations] = useState<Investigation[]>([]);
  const [availableCollectors, setAvailableCollectors] = useState<Record<string, CollectorInfo>>({});
  const [loading, setLoading] = useState(false);
  const [selectedInvestigation, setSelectedInvestigation] = useState<Investigation | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Verificar se o usuário é CLIENT
  const isClientUser = user?.role === UserRole.CLIENT;
  const effectiveCompanyId = isClientUser ? user?.company_id : selectedCompany;

  // Carregar dados da API
  // Moved below after callback definitions for proper dependencies

  // Atualizar investigações em execução a cada 5 segundos
  // Moved below after callback definitions

  const loadCollectors = useCallback(async () => {
    try {
      const response = await domainInvestigationService.getAvailableCollectors();
      setAvailableCollectors(response.collectors);
    } catch (error) {
      console.error('Erro ao carregar coletores:', error);
      toast.error(t('domainInvestigation.toasts.errorLoadingCollectors'));
    }
  }, []);

  const loadInvestigations = useCallback(async () => {
    try {
      const response = await domainInvestigationService.listInvestigations();
      setInvestigations(response.investigations);
    } catch (error) {
      console.error('Erro ao carregar investigações:', error);
      toast.error(t('domainInvestigation.toasts.errorLoadingInvestigations'));
    }
  }, []);

  // Atualizar investigações em execução a cada 5 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      const runningInvestigations = investigations.filter(
        inv => inv.status === 'started' || inv.status === 'running'
      );

      if (runningInvestigations.length > 0) {
        loadInvestigations();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [investigations, loadInvestigations]);

  const loadCompanies = useCallback(async () => {
    try {
      // Usuários CLIENT não precisam carregar lista de empresas
      if (isClientUser) {
        if (user?.company_id) {
          setSelectedCompany(user.company_id);
        }
        return;
      }

      const companiesData = await companiesService.getActive();
      setCompanies(companiesData);

      // Definir a primeira empresa como padrão
      if (companiesData.length > 0) {
        setSelectedCompany(companiesData[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error(t('domainInvestigation.toasts.errorLoadingCompanies'));
    }
  }, [isClientUser, user]);

  // Carregar dados iniciais após definir os callbacks
  useEffect(() => {
    loadCollectors();
    loadInvestigations();
    loadCompanies();
  }, [loadCollectors, loadInvestigations, loadCompanies]);

  const handleStartInvestigation = async () => {
    if (!effectiveCompanyId) {
      toast.error(t('domainInvestigation.toasts.pleaseSelectCompany'));
      return;
    }

    setLoading(true);
    try {
      const response = await domainInvestigationService.startCompanyInvestigation({
        company_id: effectiveCompanyId,
        collectors: selectedCollectors.length > 0 ? selectedCollectors : undefined,
      });

      toast.success(`${response.message} - ${response.domains_investigated.length} ${t('domainInvestigation.toasts.domainsInvestigated')}`);
      setSelectedCollectors([]);

      // Recarregar lista de investigações
      await loadInvestigations();
    } catch (error: any) {
      console.error('Erro ao iniciar investigação:', error);
      toast.error(error.response?.data?.detail || t('domainInvestigation.toasts.errorStartingInvestigation'));
    } finally {
      setLoading(false);
    }
  };

  const handleQuickScan = async () => {
    if (!effectiveCompanyId) {
      toast.error(t('domainInvestigation.toasts.pleaseSelectCompany'));
      return;
    }

    setLoading(true);
    try {
      // Se há coletores selecionados, usar todos eles
      const collectorsToUse = selectedCollectors.length > 0 ? selectedCollectors : undefined;

      const response = await domainInvestigationService.startCompanyQuickScan({
        company_id: effectiveCompanyId,
        collectors: collectorsToUse,
      });

      toast.success(`${response.message} - ${response.domains_investigated.length} ${t('domainInvestigation.toasts.domainsInvestigated')}`);
      setSelectedCollectors([]);

      // Recarregar lista de investigações
      await loadInvestigations();
    } catch (error: any) {
      console.error('Erro ao iniciar quick scan:', error);
      toast.error(error.response?.data?.detail || t('domainInvestigation.toasts.errorStartingQuickScan'));
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialInvestigation = async () => {
    if (!effectiveCompanyId) {
      toast.error(t('domainInvestigation.toasts.pleaseSelectCompany'));
      return;
    }

    setLoading(true);
    try {
      await leakedCredentialsService.startInvestigation({
        company_id: effectiveCompanyId
      });

      toast.success(t('domainInvestigation.toasts.leakedCredentialsInvestigationStarted'));

      // Opcional: redirecionar para a página de credenciais vazadas
      // window.location.href = '/leaked-credentials';
    } catch (error: any) {
      console.error('Erro ao iniciar investigação de credenciais:', error);
      toast.error(error.response?.data?.detail || t('domainInvestigation.toasts.errorStartingLeakedCredentialsInvestigation'));
    } finally {
      setLoading(false);
    }
  };

  const getCollectorIcon = (collectorId: string) => {
    const icons: Record<string, React.ReactNode> = {
      subdomain_discovery: <MagnifyingGlassIcon className='h-5 w-5' />,
      grayhatwarfare: <CloudIcon className='h-5 w-5' />,
      phishing: <ShieldExclamationIcon className='h-5 w-5' />,
      nuclei: <BugAntIcon className='h-5 w-5' />,
      dehashed: <ShieldCheckIcon className='h-5 w-5' />,
    };
    return icons[collectorId] || <ShieldExclamationIcon className='h-5 w-5' />;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className='h-5 w-5 text-green-500' />;
      case 'running':
        return <ClockIcon className='h-5 w-5 text-blue-500 animate-spin' />;
      case 'failed':
        return <ExclamationTriangleIcon className='h-5 w-5 text-red-500' />;
      default:
        return <PlayIcon className='h-5 w-5 text-gray-500' />;
    }
  };

  const handleDeleteInvestigation = async (investigationId: string) => {
    if (!window.confirm(t('domainInvestigation.toasts.confirmDeleteInvestigation'))) {
      return;
    }

    try {
      await domainInvestigationService.deleteInvestigation(investigationId);
      toast.success(t('domainInvestigation.toasts.investigationDeleted'));
      loadInvestigations();
    } catch (error) {
      console.error('Erro ao excluir investigação:', error);
      toast.error(t('domainInvestigation.toasts.errorDeletingInvestigation'));
    }
  };

  const handleViewDetails = (investigation: Investigation) => {
    setSelectedInvestigation(investigation);
    setIsDetailsModalOpen(true);
  };

  const handleDownloadReport = async (investigation: Investigation) => {
    try {
      const report = await domainInvestigationService.getInvestigationReport(investigation.investigation_id);

      const dataStr = JSON.stringify(report, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `investigation_${investigation.investigation_id}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(t('domainInvestigation.toasts.reportDownloaded'));
    } catch (error) {
      console.error('Erro ao baixar relatório:', error);
      toast.error(t('domainInvestigation.toasts.errorDownloadingReport'));
    }
  };

  // formatDate agora vem do utilitário i18nFormat para respeitar o locale atual

  return (
    <div className='p-6 max-w-7xl mx-auto'>
      <div className='mb-8'>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2'>{t('domainInvestigation.title')}</h1>
        <p className='text-gray-600'>
            {t('domainInvestigation.subtitle')}
        </p>
      </div>

      {/* Formulário de Nova Investigação */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8'>
        <h2 className='text-xl font-semibold mb-4'>{t('domainInvestigation.form.newInvestigationTitle')}</h2>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {/* Seleção de Empresa - apenas para usuários não CLIENT */}
          {!isClientUser && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                <BuildingOfficeIcon className='h-4 w-4 inline mr-1' />
                  {t('domainInvestigation.company.label')}
              </label>
              <select
                value={selectedCompany || ''}
                onChange={e => setSelectedCompany(e.target.value ? Number(e.target.value) : null)}
                className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              >
                <option value=''>{t('domainInvestigation.company.select')}</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name} ({company.domain})
                  </option>
                ))}
              </select>
              {companies.length === 0 && (
                <p className='text-sm text-red-600 mt-1'>
                    {t('domainInvestigation.company.noActive')}
                </p>
              )}
              {selectedCompany && (
                <p className='text-sm text-blue-600 mt-1'>
                    {t('domainInvestigation.company.domainsAutoFromSelectedCompany')}
                </p>
              )}
            </div>
          )}

          {/* Informação da empresa para usuários CLIENT */}
          {isClientUser && user?.company && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                <BuildingOfficeIcon className='h-4 w-4 inline mr-1' />
                  {t('domainInvestigation.company.label')}
              </label>
              <div className='w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md'>
                <span className='text-gray-900 dark:text-white'>{user.company.name}</span>
                <p className='text-sm text-blue-600 mt-1'>
                    {t('domainInvestigation.company.domainsAutoFromYourCompany')}
                </p>
              </div>
            </div>
          )}

          {/* Seleção de Coletores */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
                {t('domainInvestigation.collectors.label')}
            </label>
            <div className='grid grid-cols-2 gap-2 max-h-32 overflow-y-auto'>
              {Object.entries(availableCollectors).map(([id, collector]) => (
                <label key={id} className='flex items-center space-x-2'>
                  <input
                    type='checkbox'
                    checked={selectedCollectors.includes(id)}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedCollectors(prev => [...prev, id]);
                      } else {
                        setSelectedCollectors(prev => prev.filter(c => c !== id));
                      }
                    }}
                    className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                  />
                  <span className='text-sm'>{collector.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className='flex flex-col space-y-4 mt-6'>
          <div className='flex flex-wrap gap-4'>
            <button
              onClick={handleStartInvestigation}
              disabled={!effectiveCompanyId || loading}
              className='flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <PlayIcon className='h-4 w-4' />
              <span>{t('domainInvestigation.actions.fullInvestigation')}</span>
            </button>

            <button
              onClick={handleQuickScan}
              disabled={!effectiveCompanyId || loading}
              className='flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <MagnifyingGlassIcon className='h-4 w-4' />
              <span>{t('domainInvestigation.actions.quickScan')}</span>
            </button>

            <button
              onClick={handleCredentialInvestigation}
              disabled={!effectiveCompanyId || loading}
              className='flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <ShieldCheckIcon className='h-4 w-4' />
              <span>{t('domainInvestigation.actions.leakedCredentialsInvestigation')}</span>
            </button>
          </div>

          {/* Informação sobre o novo fluxo */}
          <div className='text-sm text-gray-600 bg-blue-50 p-3 rounded-md'>
              <strong>{t('domainInvestigation.info.newFlowTitle')}</strong> {t('domainInvestigation.info.newFlowDescription')}
          </div>

          {/* Informação sobre Scan Rápido */}
          <div className='text-sm text-gray-600 bg-green-50 p-3 rounded-md'>
              <strong>{t('domainInvestigation.info.quickScanTitle')}</strong> {selectedCollectors.length > 0
              ? t('domainInvestigation.info.quickScanSelected', { collectors: selectedCollectors.map(c => availableCollectors[c]?.name || c).join(', ') })
              : t('domainInvestigation.info.quickScanDefault')
          }
          </div>

          {/* Informação sobre Investigação de Credenciais */}
          <div className='text-sm text-gray-600 bg-red-50 p-3 rounded-md'>
              <strong>{t('domainInvestigation.info.leakedCredentialsTitle')}</strong> {t('domainInvestigation.info.leakedCredentialsDescription')}
              {' '}
            <a href='/leaked-credentials' className='text-red-600 underline'>
                {t('leakedCredentials.title')}
            </a>.
          </div>
        </div>
      </div>

      {/* Lista de Coletores Disponíveis */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8'>
        <h2 className='text-xl font-semibold mb-4'>{t('domainInvestigation.availableCollectors.title')}</h2>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {Object.entries(availableCollectors).map(([id, collector]) => (
            <div key={id} className='border border-gray-200 rounded-lg p-4'>
              <div className='flex items-center space-x-2 mb-2'>
                {getCollectorIcon(id)}
                <h3 className='font-medium'>{collector.name}</h3>
              </div>
              <p className='text-sm text-gray-600 mb-2'>{collector.description}</p>
              <div className='flex flex-wrap gap-1'>
                {collector.capabilities.map((cap, index) => (
                  <span key={`${id}-capability-${cap}-${index}`} className='px-2 py-1 bg-gray-100 text-xs rounded-full'>
                    {cap.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lista de Investigações */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md'>
        <div className='px-6 py-4 border-b border-gray-200'>
          <h2 className='text-xl font-semibold'>{t('domainInvestigation.list.title')}</h2>
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {t('domainInvestigation.table.domain')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {t('domainInvestigation.table.status')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {t('domainInvestigation.table.collectors')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {t('domainInvestigation.table.events')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {t('domainInvestigation.table.createdAt')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {t('Actions')}
                </th>
              </tr>
            </thead>
            <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
              {investigations.map((investigation, index) => (
                <tr key={`investigation-${investigation.investigation_id}-${index}`} className='hover:bg-gray-50'>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <div className='text-sm font-medium text-gray-900 dark:text-white'>{investigation.domain}</div>
                    <div className='text-sm text-gray-500'>ID: {investigation.investigation_id}</div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <div className='flex items-center space-x-2'>
                      {getStatusIcon(investigation.status)}
                      <span className='text-sm capitalize'>{investigation.status}</span>
                    </div>
                  </td>
                  <td className='px-6 py-4'>
                    <div className='flex flex-wrap gap-1'>
                      {investigation.collectors_used.map((collector, index) => (
                        <span
                          key={`${investigation.investigation_id}-collector-${collector}-${index}`}
                          className='inline-flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full'
                        >
                          {getCollectorIcon(collector)}
                          <span>{availableCollectors[collector]?.name || collector}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <div className='text-sm text-gray-900 dark:text-white'>{t('domainInvestigation.table.total')}: {investigation.total_events}</div>
                    {investigation.total_events > 0 && (
                      <div className='text-xs text-gray-500'>
                        <span className='text-red-600'>{t('common.severity.high')}: {investigation.high_severity}</span>
                        {' | '}
                        <span className='text-yellow-600'>{t('common.severity.medium')}: {investigation.medium_severity}</span>
                        {' | '}
                        <span className='text-green-600'>{t('common.severity.low')}: {investigation.low_severity}</span>
                      </div>
                    )}
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                    {formatDate(investigation.created_at)}
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                    <div className='flex space-x-2'>
                      <button
                        onClick={() => handleViewDetails(investigation)}
                        className='text-blue-600 hover:text-blue-900'
                        title={t('View')}
                      >
                        <EyeIcon className='h-4 w-4' />
                      </button>
                      {investigation.status === 'completed' && (
                        <button
                          onClick={() => handleDownloadReport(investigation)}
                          className='text-green-600 hover:text-green-900'
                          title={t('Download')}
                        >
                          <DocumentTextIcon className='h-4 w-4' />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteInvestigation(investigation.investigation_id)}
                        className='text-red-600 hover:text-red-900'
                        title={t('Delete')}
                      >
                        <TrashIcon className='h-4 w-4' />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Detalhes */}
      {selectedInvestigation && (
        <InvestigationDetailsModal
          investigation={selectedInvestigation}
          isOpen={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedInvestigation(null);
          }}
        />
      )}
    </div>
  );
};

export default DomainInvestigation;
